import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import type { BrandResponse, StoreResponse } from '@clarity/contract';
import { useBrands, useStores } from '../queries/index.js';
import { SURFACE_CAPABILITY, type SurfaceId } from '../features/nav.js';
import { useClientContext } from './clientContext.js';
import { useSession } from './session.js';
import { isNotYetOnboarded } from './lifecycle.js';

export type { SurfaceId };

/**
 * The URL-derived SCOPE context (clarity-navigation-scope Req 1.4, 3.1) — a
 * sibling to {@link useClientContext}. Scope is the second axis after client:
 * within one client, the user is looking at ALL its branches, one BRAND's
 * branches, or a single BRANCH (store). Like the client, it lives in the URL as
 * a path segment (never React state that could desync):
 *
 *   /c/:clientId/all/<surface>
 *   /c/:clientId/brand/:brandId/<surface>
 *   /c/:clientId/store/:storeId/<surface>
 *
 * This context reads that segment and resolves the set of branches the scope
 * covers by REUSING the existing admin client-narrowing (the same `useStores()`
 * + `useBrands()` filter-by-`brand.clientId` pattern used in `Dashboard` and the
 * old `StorePicker`). The server still enforces tenant isolation; this narrowing
 * is a UX convenience and the source of the "unknown brand/store ⇒ not-found"
 * rule (Req 3.4 / 5.3 / 8.2).
 */

// ─── The scope model (design §3) ──────────────────────────────────────────────

/** The three scope levels within a client, derived from the URL. */
export type Scope =
  | { kind: 'all' }
  | { kind: 'brand'; brandId: string }
  | { kind: 'store'; storeId: string };

export interface BrandGroup {
  brand: BrandResponse;
  branches: StoreResponse[];
}

export interface ScopeContextValue {
  /** The resolved scope, derived from the URL (defaults to `all`). */
  scope: Scope;
  /** The tenant-/client-narrowed branches this scope covers. */
  branches: StoreResponse[];
  /** The brand when `scope.kind === 'brand'` (resolved for breadcrumbs), else null. */
  brand: BrandResponse | null;
  /** The branch when `scope.kind === 'store'`, else null. */
  store: StoreResponse | null;
  /** True when the scope resolves to exactly one branch (one-branch brand / store). */
  isSingleBranch: boolean;
  /**
   * True when the scope resolves to a SINGLE branch that is not-yet-onboarded
   * (`isNotYetOnboarded`) — the onboarding-lockdown signal (Req 5.2). The Shell
   * reads this to collapse the rail to the onboarding surface only (task 4.2);
   * the `OnboardingLockGuard` (task 3.4) enforces the matching URL redirect.
   *
   * NOTE: this is derived from the stores LIST (the same narrowed set the rest of
   * the context uses), so it may lag a just-completed onboarding by one list
   * refetch. The route guard re-reads the single store (`useStore`) for the
   * authoritative, freshest lock decision; this flag is the rail-collapse hint.
   */
  isOnboardingLocked: boolean;
  /**
   * True when the URL named a brand/store that is NOT in the active client's set
   * (out-of-tenant or deleted). The scope guard (task 3.2) redirects these to the
   * default scope; this context already falls back to `all` so nothing crashes.
   */
  notFound: boolean;
  /**
   * Navigate to another scope, PRESERVING the current surface where the target
   * scope's capability + onboarding allow it, else falling back to `dashboard`
   * (Req 1.3). Pass the current surface explicitly (the shell/switcher knows it).
   */
  goToScope: (scope: Scope, surface?: SurfaceId) => void;
  /** The branch set grouped by brand, for the switcher's brand headers (Req 2.2). */
  brandGroups: BrandGroup[];
}

// ─── Capability + onboarding helpers (design §4, §5) ──────────────────────────

/**
 * Whether `surface` can be opened at `scope` given the resolved `branches`, so
 * `goToScope` keeps the surface or falls back to `dashboard` (Req 1.3):
 *
 * - a BRANCH-ONLY surface (catalog/onboarding) needs a single resolved branch;
 * - a not-yet-onboarded single branch may ONLY open `onboarding` (Req 5.3);
 * - everything else (flexible surfaces at any scope) is allowed.
 */
export function surfaceAvailable(
  surface: SurfaceId,
  branches: StoreResponse[],
): boolean {
  const single = branches.length === 1 ? branches[0] : null;
  if (single && isNotYetOnboarded(single)) {
    // A locked single branch offers only onboarding (design §8 truth table).
    return surface === 'onboarding';
  }
  if (SURFACE_CAPABILITY[surface] === 'branch-only') {
    // Branch-only surfaces are reachable only when the scope is one branch;
    // otherwise the branch-only guard (task 3.3) will narrow / prompt.
    return branches.length === 1;
  }
  return true;
}

// ─── Pure resolution (design §3) ──────────────────────────────────────────────

export interface ScopeResolution {
  scope: Scope;
  branches: StoreResponse[];
  brand: BrandResponse | null;
  store: StoreResponse | null;
  isSingleBranch: boolean;
  isOnboardingLocked: boolean;
  notFound: boolean;
  brandGroups: BrandGroup[];
}

/**
 * The onboarding-lockdown predicate over a resolved branch set (Req 5.2): a scope
 * is locked only when it resolves to EXACTLY ONE branch and that branch is
 * not-yet-onboarded. A multi-branch all/brand scope is never locked — the
 * aggregate simply excludes un-onboarded branches (Req 5.6).
 */
function isScopeLocked(branches: StoreResponse[]): boolean {
  return branches.length === 1 && isNotYetOnboarded(branches[0] as StoreResponse);
}

/**
 * Resolve the scope + its branch set from the URL segments and the loaded
 * brand/store lists, applying the same client-narrowing the dashboard uses.
 *
 * Pure (no hooks) so it is unit-testable in isolation:
 *
 * 1. Narrow brands/branches to the active client — only an admin viewing one
 *    selected client narrows; a client-role user trusts the server's tenant
 *    scoping (its lists already contain only its own).
 * 2. Interpret `scopeKind` + the id segment:
 *    - `all`   → every in-client branch;
 *    - `brand` → branches with `brandId === :brandId` (and that brand belongs to
 *      the client, else `notFound`);
 *    - `store` → the single `:storeId` branch (else `notFound`);
 *    - anything else → defaults to `all`.
 * 3. Always compute `brandGroups` over the in-client branch set for the switcher.
 */
export function resolveScope(
  params: {
    scopeKind?: string | undefined;
    brandId?: string | undefined;
    storeId?: string | undefined;
  },
  allBrands: BrandResponse[],
  allStores: StoreResponse[],
  opts: { role: string | null; selectedClientId: string | null },
): ScopeResolution {
  const narrow = opts.role === 'admin' && opts.selectedClientId !== null;
  const clientBrands = narrow
    ? allBrands.filter((b) => b.clientId === opts.selectedClientId)
    : allBrands;
  const clientBrandIds = new Set(clientBrands.map((b) => b.id));
  const clientBranches = narrow
    ? allStores.filter((s) => clientBrandIds.has(s.brandId))
    : allStores;

  // The brand grouping the switcher shows is the same brand set as the brand
  // scope (Req 3.5): one group per in-client brand, with its branches beneath.
  const brandGroups: BrandGroup[] = clientBrands.map((brand) => ({
    brand,
    branches: clientBranches.filter((s) => s.brandId === brand.id),
  }));

  const kind = params.scopeKind;

  if (kind === 'brand') {
    const brandId = params.brandId;
    const brand = clientBrands.find((b) => b.id === brandId) ?? null;
    if (!brand) {
      // Unknown / out-of-tenant brand ⇒ not-found; fall back to the all scope so
      // nothing renders cross-tenant while the guard redirects (Req 3.4 / 8.2).
      return {
        scope: { kind: 'all' },
        branches: clientBranches,
        brand: null,
        store: null,
        isSingleBranch: clientBranches.length === 1,
        isOnboardingLocked: isScopeLocked(clientBranches),
        notFound: true,
        brandGroups,
      };
    }
    const branches = clientBranches.filter((s) => s.brandId === brand.id);
    return {
      scope: { kind: 'brand', brandId: brand.id },
      branches,
      brand,
      store: null,
      isSingleBranch: branches.length === 1,
      isOnboardingLocked: isScopeLocked(branches),
      notFound: false,
      brandGroups,
    };
  }

  if (kind === 'store') {
    const storeId = params.storeId;
    const store = clientBranches.find((s) => s.id === storeId) ?? null;
    if (!store) {
      // Unknown / out-of-tenant store ⇒ not-found (Req 5.3 / 8.2).
      return {
        scope: { kind: 'all' },
        branches: clientBranches,
        brand: null,
        store: null,
        isSingleBranch: clientBranches.length === 1,
        isOnboardingLocked: isScopeLocked(clientBranches),
        notFound: true,
        brandGroups,
      };
    }
    return {
      scope: { kind: 'store', storeId: store.id },
      branches: [store],
      // Resolve the store's brand for breadcrumbs (may be absent if its brand is
      // out of the narrowed set, which `clientBranches` already excludes).
      brand: clientBrands.find((b) => b.id === store.brandId) ?? null,
      store,
      isSingleBranch: true,
      isOnboardingLocked: isNotYetOnboarded(store),
      notFound: false,
      brandGroups,
    };
  }

  // `all` (and any unrecognized segment) ⇒ every in-client branch.
  return {
    scope: { kind: 'all' },
    branches: clientBranches,
    brand: null,
    store: null,
    isSingleBranch: clientBranches.length === 1,
    isOnboardingLocked: isScopeLocked(clientBranches),
    notFound: false,
    brandGroups,
  };
}

/**
 * Build the path for a scope + surface, preserving `surface` only where it is
 * available at the TARGET scope (Req 1.3). The caller passes the branches the
 * target scope will resolve to, so a branch-only or locked surface downgrades to
 * `dashboard` before navigation (rather than bouncing through a guard).
 */
export function scopePath(
  clientId: string,
  scope: Scope,
  surface: SurfaceId,
  targetBranches: StoreResponse[],
): string {
  const safeSurface = surfaceAvailable(surface, targetBranches) ? surface : 'dashboard';
  switch (scope.kind) {
    case 'all':
      return `/c/${clientId}/all/${safeSurface}`;
    case 'brand':
      return `/c/${clientId}/brand/${scope.brandId}/${safeSurface}`;
    case 'store':
      return `/c/${clientId}/store/${scope.storeId}/${safeSurface}`;
  }
}

/**
 * Given the loaded brand/store lists, compute the branches a prospective scope
 * would cover — used by `goToScope` to validate surface preservation against the
 * TARGET scope (not the current one).
 */
function branchesForScope(
  scope: Scope,
  clientBranches: StoreResponse[],
): StoreResponse[] {
  switch (scope.kind) {
    case 'all':
      return clientBranches;
    case 'brand':
      return clientBranches.filter((s) => s.brandId === scope.brandId);
    case 'store': {
      const s = clientBranches.find((b) => b.id === scope.storeId);
      return s ? [s] : [];
    }
  }
}

// ─── React context ────────────────────────────────────────────────────────────

const ScopeContextCtx = createContext<ScopeContextValue | null>(null);

export function ScopeProvider({ children }: { children: ReactNode }) {
  const { role } = useSession();
  const { activeClientId } = useClientContext();
  const navigate = useNavigate();

  // Read the scope segments from the URL via a path match, so this works even
  // though the provider sits ABOVE the <Route> that declares the params. The
  // `/*` splat lets the surface tail (dashboard/orders/…) sit beyond the match.
  const match = useMatch('/c/:clientId/:scopeKind/*');
  const scopeKind = match?.params.scopeKind;
  // `brand`/`store` scopes carry the id in the FIRST splat segment
  // (`brand/:brandId/...` ⇒ splat starts with `:brandId`).
  const splat = match?.params['*'] ?? '';
  const firstSplat = splat.split('/')[0] || undefined;
  const brandId = scopeKind === 'brand' ? firstSplat : undefined;
  const storeId = scopeKind === 'store' ? firstSplat : undefined;

  const brands = useBrands();
  const stores = useStores();

  const value = useMemo<ScopeContextValue>(() => {
    const brandRows: BrandResponse[] = brands.data?.status === 200 ? brands.data.body : [];
    const storeRows: StoreResponse[] = stores.data?.status === 200 ? stores.data.body : [];

    const resolved = resolveScope(
      { scopeKind, brandId, storeId },
      brandRows,
      storeRows,
      { role, selectedClientId: activeClientId },
    );

    // The narrowed client sets, recomputed once for `goToScope`'s target checks.
    const narrow = role === 'admin' && activeClientId !== null;
    const clientBrands = narrow
      ? brandRows.filter((b) => b.clientId === activeClientId)
      : brandRows;
    const clientBrandIds = new Set(clientBrands.map((b) => b.id));
    const clientBranches = narrow
      ? storeRows.filter((s) => clientBrandIds.has(s.brandId))
      : storeRows;

    const goToScope: ScopeContextValue['goToScope'] = (next, surface = 'dashboard') => {
      if (!activeClientId) return;
      const targetBranches = branchesForScope(next, clientBranches);
      navigate(scopePath(activeClientId, next, surface, targetBranches));
    };

    return {
      scope: resolved.scope,
      branches: resolved.branches,
      brand: resolved.brand,
      store: resolved.store,
      isSingleBranch: resolved.isSingleBranch,
      isOnboardingLocked: resolved.isOnboardingLocked,
      notFound: resolved.notFound,
      brandGroups: resolved.brandGroups,
      goToScope,
    };
  }, [brands.data, stores.data, scopeKind, brandId, storeId, role, activeClientId, navigate]);

  return <ScopeContextCtx.Provider value={value}>{children}</ScopeContextCtx.Provider>;
}

export function useScopeContext(): ScopeContextValue {
  const ctx = useContext(ScopeContextCtx);
  if (!ctx) {
    throw new Error('useScopeContext must be used within a ScopeProvider');
  }
  return ctx;
}

/**
 * A tolerant read of the scope context that returns `null` OUTSIDE a
 * `<ScopeProvider>` instead of throwing (clarity-navigation-scope task 6.3).
 *
 * Every workspace surface now mounts inside the scope tree (via
 * `WorkspaceSurface`), so the Dashboard reads the resolved scope through
 * {@link useScopeContext} in practice — the legacy `/c/:clientId/dashboard`
 * route is gone (clarity-navigation-scope task 7.1). This tolerant variant is
 * kept because the Dashboard is still unit-tested OUTSIDE a `<ScopeProvider>`
 * (`Dashboard.test.tsx` renders it on a bare legacy-shaped route): there
 * `useScopeContext()` would throw, while this hook returns `null` and the
 * Dashboard falls back to the whole-client narrowing — without the brittle
 * try/catch-around-a-hook pattern (which breaks the rules of hooks). It is
 * harmless to keep and avoids churn in the Dashboard + its test.
 */
export function useScopeContextOptional(): ScopeContextValue | null {
  return useContext(ScopeContextCtx);
}
